/**
 * `useScopedBoxes` — like `useBoxes`, but the returned snapshot's `boxes` are
 * filtered to the SCUs the current user may actually see (vw_user_scu_access,
 * via GET /api/me/scus). Use this for box dropdowns / searches / pickers so
 * they honor the user's permissions/role/customer scope for EVERY role —
 * platform included (the operator snapshot is the whole fleet, so it needs the
 * explicit filter; customer/vendor are already server-scoped, so it's a no-op
 * for them). The operator fleet views (Map/Boxes/Outages) keep `useBoxes`.
 */

import { useEffect, useMemo, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { withRecomputedCounts } from "../lib/stateCounts";
import { fetchVisibleScus } from "../services/mttrApi";

import { useBoxes, type UseBoxesResult } from "./useBoxes";

export function useScopedBoxes(): UseBoxesResult {
  const base = useBoxes();
  const { token } = useAuth();
  const [visible, setVisible] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchVisibleScus(token)
      .then((scus) => {
        if (!cancelled) setVisible(new Set(scus));
      })
      .catch(() => {
        // On failure, leave null -> fall back to the unfiltered snapshot
        // (which for customer/vendor is already server-scoped anyway).
        if (!cancelled) setVisible(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const snapshot = useMemo(() => {
    if (!base.snapshot || !visible) return base.snapshot;
    // Filter the box list, then re-derive total_boxes / state_counts /
    // stacked_count so downstream consumers see counts that match the
    // filtered set (Dashboard donut, Map chips, BoxesPage chips).
    return withRecomputedCounts({
      ...base.snapshot,
      boxes: base.snapshot.boxes.filter((b) => visible.has(b.scu_name)),
    });
  }, [base.snapshot, visible]);

  return { ...base, snapshot };
}
